import { supabase } from './supabase';

export interface CoachRanking {
  coachId: string;
  coachName: string;
  completedAssessments: number;
}

export interface CodeRedemption {
  totalCodes: number;
  usedCodes: number;
  rate: number;
}

export interface MonthlyAssessment {
  month: string;
  label: string;
  count: number;
}

export interface RecentCompletion {
  id: string;
  respondentName: string;
  coachName: string;
  completedAt: string;
}

export interface TeamCompletion {
  teamId: string;
  teamName: string;
  totalMembers: number;
  completedMembers: number;
  rate: number;
}

export interface InactiveCoach {
  coachId: string;
  coachName: string;
  lastActivityDate: string | null;
}

export async function fetchTopCoaches(partnerId: string): Promise<CoachRanking[]> {
  const { data: coaches } = await supabase
    .from('coaches')
    .select('id, users!coaches_user_id_fkey(full_name, email)')
    .eq('partner_id', partnerId);

  if (!coaches || coaches.length === 0) return [];

  const coachIds = coaches.map(c => c.id);

  const { data: results } = await supabase
    .from('results')
    .select('coach_id')
    .in('coach_id', coachIds);

  const countMap: Record<string, number> = {};
  (results || []).forEach(r => {
    if (r.coach_id) {
      countMap[r.coach_id] = (countMap[r.coach_id] || 0) + 1;
    }
  });

  return coaches
    .map(c => ({
      coachId: c.id,
      coachName: (c.users as any)?.full_name || (c.users as any)?.email || 'Unknown',
      completedAssessments: countMap[c.id] || 0,
    }))
    .sort((a, b) => b.completedAssessments - a.completedAssessments)
    .slice(0, 10);
}

export async function fetchCodeRedemption(partnerId: string): Promise<CodeRedemption> {
  const { count: totalCodes } = await supabase
    .from('assessment_codes')
    .select('*', { count: 'exact', head: true })
    .eq('partner_id', partnerId);

  const { count: usedCodes } = await supabase
    .from('assessment_codes')
    .select('*', { count: 'exact', head: true })
    .eq('partner_id', partnerId)
    .eq('is_used', true);

  const total = totalCodes || 0;
  const used = usedCodes || 0;

  return {
    totalCodes: total,
    usedCodes: used,
    rate: total > 0 ? Math.round((used / total) * 100) : 0,
  };
}

export async function fetchAssessmentsOverTime(partnerId: string): Promise<MonthlyAssessment[]> {
  const { data: coaches } = await supabase
    .from('coaches')
    .select('id')
    .eq('partner_id', partnerId);

  if (!coaches || coaches.length === 0) return [];

  const coachIds = coaches.map(c => c.id);

  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 5);
  sixMonthsAgo.setDate(1);
  sixMonthsAgo.setHours(0, 0, 0, 0);

  const { data: results } = await supabase
    .from('results')
    .select('completed_at')
    .in('coach_id', coachIds)
    .gte('completed_at', sixMonthsAgo.toISOString())
    .order('completed_at', { ascending: true });

  const monthMap: Record<string, number> = {};
  const months: { month: string; label: string }[] = [];

  for (let i = 0; i < 6; i++) {
    const d = new Date();
    d.setMonth(d.getMonth() - (5 - i));
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('default', { month: 'short' });
    months.push({ month: key, label });
    monthMap[key] = 0;
  }

  (results || []).forEach(r => {
    if (r.completed_at) {
      const d = new Date(r.completed_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (monthMap[key] !== undefined) {
        monthMap[key]++;
      }
    }
  });

  return months.map(m => ({
    month: m.month,
    label: m.label,
    count: monthMap[m.month],
  }));
}

export async function fetchRecentCompletions(partnerId: string): Promise<RecentCompletion[]> {
  const { data: coaches } = await supabase
    .from('coaches')
    .select('id, users!coaches_user_id_fkey(full_name, email)')
    .eq('partner_id', partnerId);

  if (!coaches || coaches.length === 0) return [];

  const coachIds = coaches.map(c => c.id);
  const coachNameMap: Record<string, string> = {};
  coaches.forEach(c => {
    coachNameMap[c.id] = (c.users as any)?.full_name || (c.users as any)?.email || 'Unknown';
  });

  const { data: results } = await supabase
    .from('results')
    .select('id, user_id, coach_id, completed_at, users!results_user_id_fkey(full_name, email)')
    .in('coach_id', coachIds)
    .order('completed_at', { ascending: false })
    .limit(15);

  return (results || []).map(r => ({
    id: r.id,
    respondentName: (r.users as any)?.full_name || (r.users as any)?.email || 'Unknown',
    coachName: r.coach_id ? coachNameMap[r.coach_id] || 'Unknown' : 'Unknown',
    completedAt: r.completed_at || '',
  }));
}

export async function fetchTeamCompletions(partnerId: string): Promise<TeamCompletion[]> {
  const { data: teams } = await supabase
    .from('teams')
    .select('id, name')
    .eq('partner_id', partnerId)
    .eq('is_active', true);

  if (!teams || teams.length === 0) return [];

  const teamIds = teams.map(t => t.id);

  const { data: members } = await supabase
    .from('team_members')
    .select('team_id, user_id')
    .in('team_id', teamIds);

  if (!members || members.length === 0) {
    return teams.map(t => ({
      teamId: t.id,
      teamName: t.name,
      totalMembers: 0,
      completedMembers: 0,
      rate: 0,
    }));
  }

  const teamMemberMap: Record<string, string[]> = {};
  members.forEach(m => {
    if (!teamMemberMap[m.team_id]) teamMemberMap[m.team_id] = [];
    teamMemberMap[m.team_id].push(m.user_id);
  });

  const allUserIds = [...new Set(members.map(m => m.user_id))];

  const { data: results } = await supabase
    .from('results')
    .select('user_id')
    .in('user_id', allUserIds);

  const usersWithResults = new Set((results || []).map(r => r.user_id));

  return teams.map(t => {
    const memberUserIds = teamMemberMap[t.id] || [];
    const total = memberUserIds.length;
    const completed = memberUserIds.filter(uid => usersWithResults.has(uid)).length;
    return {
      teamId: t.id,
      teamName: t.name,
      totalMembers: total,
      completedMembers: completed,
      rate: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  });
}

export interface CoachActivityStat {
  coachId: string;
  coachName: string;
  coachEmail: string;
  totalRespondents: number;
  completedAssessments: number;
  lastActiveDate: string | null;
  coachAddedDate: string | null;
}

export async function fetchCoachActivityStats(partnerId: string): Promise<CoachActivityStat[]> {
  const { data: coaches } = await supabase
    .from('coaches')
    .select('id, created_at, users!coaches_user_id_fkey(full_name, email)')
    .eq('partner_id', partnerId);

  if (!coaches || coaches.length === 0) return [];

  const coachIds = coaches.map(c => c.id);

  const [respondentsRes, resultsRes] = await Promise.all([
    supabase
      .from('respondents')
      .select('coach_id')
      .in('coach_id', coachIds),
    supabase
      .from('results')
      .select('coach_id, completed_at')
      .in('coach_id', coachIds)
      .order('completed_at', { ascending: false }),
  ]);

  const respondentCountMap: Record<string, number> = {};
  (respondentsRes.data || []).forEach(r => {
    if (r.coach_id) {
      respondentCountMap[r.coach_id] = (respondentCountMap[r.coach_id] || 0) + 1;
    }
  });

  const completionCountMap: Record<string, number> = {};
  const lastActiveDateMap: Record<string, string | null> = {};
  coachIds.forEach(id => { lastActiveDateMap[id] = null; });
  (resultsRes.data || []).forEach(r => {
    if (r.coach_id) {
      completionCountMap[r.coach_id] = (completionCountMap[r.coach_id] || 0) + 1;
      if (!lastActiveDateMap[r.coach_id]) {
        lastActiveDateMap[r.coach_id] = r.completed_at;
      }
    }
  });

  return coaches.map(c => ({
    coachId: c.id,
    coachName: (c.users as any)?.full_name || (c.users as any)?.email || 'Unknown',
    coachEmail: (c.users as any)?.email || '',
    totalRespondents: respondentCountMap[c.id] || 0,
    completedAssessments: completionCountMap[c.id] || 0,
    lastActiveDate: lastActiveDateMap[c.id],
    coachAddedDate: c.created_at || null,
  }));
}

export async function fetchInactiveCoaches(partnerId: string): Promise<InactiveCoach[]> {
  const { data: coaches } = await supabase
    .from('coaches')
    .select('id, users!coaches_user_id_fkey(full_name, email)')
    .eq('partner_id', partnerId)
    .eq('is_active', true);

  if (!coaches || coaches.length === 0) return [];

  const coachIds = coaches.map(c => c.id);

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const { data: recentResults } = await supabase
    .from('results')
    .select('coach_id, completed_at')
    .in('coach_id', coachIds)
    .order('completed_at', { ascending: false });

  const lastActivityMap: Record<string, string | null> = {};
  coachIds.forEach(id => { lastActivityMap[id] = null; });

  (recentResults || []).forEach(r => {
    if (r.coach_id && !lastActivityMap[r.coach_id]) {
      lastActivityMap[r.coach_id] = r.completed_at;
    }
  });

  return coaches
    .filter(c => {
      const lastDate = lastActivityMap[c.id];
      if (!lastDate) return true;
      return new Date(lastDate) < thirtyDaysAgo;
    })
    .map(c => ({
      coachId: c.id,
      coachName: (c.users as any)?.full_name || (c.users as any)?.email || 'Unknown',
      lastActivityDate: lastActivityMap[c.id],
    }));
}

// ============================================================
// NLP Practice AI Usage
// ============================================================

export const VOICE_COST_PER_MINUTE = 0.08;
export const CHAT_COST_PER_1K_TOKENS = 0.01;

export function calcVoiceCost(voiceMinutes: number): number {
  return Math.round(voiceMinutes * VOICE_COST_PER_MINUTE * 100) / 100;
}

export function calcChatCost(chatTokens: number): number {
  return Math.round((chatTokens / 1000) * CHAT_COST_PER_1K_TOKENS * 100) / 100;
}

export interface NlpPracticeUsage {
  coachId: string;
  coachName: string;
  chatTokens: number;
  voiceSessions: number;
  voiceMinutes: number;
  chatSessions: number;
  totalSessions: number;
  lastUsedAt: string | null;
  voiceCost: number;
  chatCost: number;
  totalCost: number;
}

export interface NlpPracticeUsageSummary {
  totalChatTokens: number;
  totalVoiceSessions: number;
  totalVoiceMinutes: number;
  totalChatSessions: number;
  totalSessions: number;
  activeCoaches: number;
  totalVoiceCost: number;
  totalChatCost: number;
  totalCost: number;
  coaches: NlpPracticeUsage[];
}

export async function fetchNlpPracticeUsage(partnerId: string, monthDate?: Date): Promise<NlpPracticeUsageSummary> {
  const { data: coaches } = await supabase
    .from('coaches')
    .select('id, users!coaches_user_id_fkey(full_name, email)')
    .eq('partner_id', partnerId);

  if (!coaches || coaches.length === 0) {
    return { totalChatTokens: 0, totalVoiceSessions: 0, totalVoiceMinutes: 0, totalChatSessions: 0, totalSessions: 0, activeCoaches: 0, totalVoiceCost: 0, totalChatCost: 0, totalCost: 0, coaches: [] };
  }

  const coachNameMap: Record<string, string> = {};
  coaches.forEach(c => {
    coachNameMap[c.id] = (c.users as any)?.full_name || (c.users as any)?.email || 'Unknown';
  });

  const monthStart = monthDate || new Date();
  monthStart.setDate(1);
  monthStart.setHours(0, 0, 0, 0);

  const { data: logs } = await (supabase as any)
    .from('nlp_practice_usage_logs')
    .select('coach_id, type, token_input, token_output, duration_seconds, created_at')
    .eq('partner_id', partnerId)
    .gte('created_at', monthStart.toISOString())
    .order('created_at', { ascending: false });

  const coachMap: Record<string, NlpPracticeUsage> = {};
  coaches.forEach(c => {
    coachMap[c.id] = {
      coachId: c.id,
      coachName: coachNameMap[c.id],
      chatTokens: 0,
      voiceSessions: 0,
      voiceMinutes: 0,
      chatSessions: 0,
      totalSessions: 0,
      lastUsedAt: null,
      voiceCost: 0,
      chatCost: 0,
      totalCost: 0,
    };
  });

  (logs || []).forEach((log: any) => {
    const entry = coachMap[log.coach_id];
    if (!entry) return;
    if (log.type === 'chat') {
      entry.chatTokens += (log.token_input || 0) + (log.token_output || 0);
      entry.chatSessions += 1;
    } else if (log.type === 'voice') {
      entry.voiceSessions += 1;
      entry.voiceMinutes += Math.round((log.duration_seconds || 0) / 60 * 10) / 10;
    }
    entry.totalSessions += 1;
    if (!entry.lastUsedAt) {
      entry.lastUsedAt = log.created_at;
    }
  });

  const coachList = Object.values(coachMap).sort((a, b) => b.totalSessions - a.totalSessions);
  const activeCoaches = coachList.filter(c => c.totalSessions > 0).length;

  coachList.forEach(c => {
    c.voiceCost = calcVoiceCost(c.voiceMinutes);
    c.chatCost = calcChatCost(c.chatTokens);
    c.totalCost = Math.round((c.voiceCost + c.chatCost) * 100) / 100;
  });

  const totalVoiceCost = Math.round(coachList.reduce((sum, c) => sum + c.voiceCost, 0) * 100) / 100;
  const totalChatCost = Math.round(coachList.reduce((sum, c) => sum + c.chatCost, 0) * 100) / 100;

  return {
    totalChatTokens: coachList.reduce((sum, c) => sum + c.chatTokens, 0),
    totalVoiceSessions: coachList.reduce((sum, c) => sum + c.voiceSessions, 0),
    totalVoiceMinutes: Math.round(coachList.reduce((sum, c) => sum + c.voiceMinutes, 0) * 10) / 10,
    totalChatSessions: coachList.reduce((sum, c) => sum + c.chatSessions, 0),
    totalSessions: coachList.reduce((sum, c) => sum + c.totalSessions, 0),
    activeCoaches,
    totalVoiceCost,
    totalChatCost,
    totalCost: Math.round((totalVoiceCost + totalChatCost) * 100) / 100,
    coaches: coachList,
  };
}
